import React, { useState, useEffect, useRef } from 'react';
import { 
  Shield, Users, FileText, MessageSquare, DollarSign, 
  Newspaper, Trash2, Eye, EyeOff, Check, X, RefreshCw,
  ChevronLeft, LogOut, BarChart3, Settings, Image, Upload, FileVideo, Download, ShoppingBag, Edit, Save
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminDashboard = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [adminKey, setAdminKey] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(false);
  const adminFileInputRef = useRef(null);
  
  // Data states
  const [stats, setStats] = useState(null);
  const [posts, setPosts] = useState([]);
  const [comments, setComments] = useState([]);
  const [news, setNews] = useState([]);
  const [referralApplications, setReferralApplications] = useState([]);
  const [referralAccounts, setReferralAccounts] = useState([]);
  const [pendingMedia, setPendingMedia] = useState([]);
  const [approvedMedia, setApprovedMedia] = useState([]);
  const [players, setPlayers] = useState([]);
  const [teams, setTeams] = useState([]);
  const [merchProducts, setMerchProducts] = useState([]);
  
  // News form
  const [newNewsTitle, setNewNewsTitle] = useState('');
  const [newNewsLink, setNewNewsLink] = useState('');
  
  // Merch form
  const [newMerchName, setNewMerchName] = useState('');
  const [newMerchPrice, setNewMerchPrice] = useState('');
  const [newMerchImage, setNewMerchImage] = useState('');
  const [newMerchLink, setNewMerchLink] = useState('');
  
  // Admin upload form
  const [adminUploadFiles, setAdminUploadFiles] = useState([]);
  const [adminUploadPreviews, setAdminUploadPreviews] = useState([]);
  const [adminUploadCaption, setAdminUploadCaption] = useState('');
  const [adminUploadTeam, setAdminUploadTeam] = useState('');
  const [adminUploadSponsored, setAdminUploadSponsored] = useState(false);
  const [adminUploading, setAdminUploading] = useState(false);
  
  // Edit player/team modal states
  const [editingPlayer, setEditingPlayer] = useState(null);
  const [editingTeam, setEditingTeam] = useState(null);
  const [editFormData, setEditFormData] = useState({});
  
  // Edit media modal state
  const [editingMedia, setEditingMedia] = useState(null);
  const [mediaEditData, setMediaEditData] = useState({});

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ admin_key: adminKey })
      });
      const data = await response.json();
      if (data.valid) {
        setIsAuthenticated(true);
        localStorage.setItem('gwins_admin_key', adminKey);
        toast.success('Admin access granted');
        loadDashboardData();
      } else {
        toast.error('Invalid admin key');
      }
    } catch (error) {
      toast.error('Authentication failed');
    }
    setLoading(false);
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    setAdminKey('');
    localStorage.removeItem('gwins_admin_key');
    toast.success('Logged out');
  };

  useEffect(() => {
    const savedKey = localStorage.getItem('gwins_admin_key');
    if (savedKey) {
      setAdminKey(savedKey);
      verifyAndLoad(savedKey);
    }
  }, []);

  const verifyAndLoad = async (key) => {
    try {
      const response = await fetch(`${API_URL}/api/admin/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ admin_key: key })
      });
      const data = await response.json();
      if (data.valid) {
        setIsAuthenticated(true);
        loadDashboardData(key);
      }
    } catch (error) {
      console.error('Auto-login failed');
    }
  };

  const loadDashboardData = async (key = adminKey) => {
    setLoading(true);
    try {
      // Load stats
      const statsRes = await fetch(`${API_URL}/api/admin/stats?admin_key=${key}`);
      const statsData = await statsRes.json();
      setStats(statsData);

      // Load posts
      const postsRes = await fetch(`${API_URL}/api/admin/posts?admin_key=${key}&limit=100`);
      const postsData = await postsRes.json();
      setPosts(postsData.posts || []);

      // Load comments
      const commentsRes = await fetch(`${API_URL}/api/admin/comments?admin_key=${key}&limit=100`);
      const commentsData = await commentsRes.json();
      setComments(commentsData.comments || []);

      // Load news
      const newsRes = await fetch(`${API_URL}/api/admin/news?admin_key=${key}`);
      const newsData = await newsRes.json();
      setNews(newsData.news || []);

      // Load referral applications
      const appsRes = await fetch(`${API_URL}/api/referrals/admin/applications?status=pending`);
      const appsData = await appsRes.json();
      setReferralApplications(appsData.applications || []);

      // Load referral accounts
      const accountsRes = await fetch(`${API_URL}/api/referrals/admin/accounts`);
      const accountsData = await accountsRes.json();
      setReferralAccounts(accountsData.accounts || []);

      // Load pending media
      const mediaRes = await fetch(`${API_URL}/api/media/admin/pending?admin_key=${key}`);
      const mediaData = await mediaRes.json();
      setPendingMedia(mediaData.media || []);

      // Load approved/published media
      const approvedMediaRes = await fetch(`${API_URL}/api/media/admin/all?admin_key=${key}&status=approved&limit=100`);
      const approvedMediaData = await approvedMediaRes.json();
      setApprovedMedia(approvedMediaData.media || []);

      // Load players
      const playersRes = await fetch(`${API_URL}/api/admin/players?admin_key=${key}&limit=200`);
      const playersData = await playersRes.json();
      setPlayers(playersData.players || []);

      // Load teams
      const teamsRes = await fetch(`${API_URL}/api/admin/teams?admin_key=${key}&limit=200`);
      const teamsData = await teamsRes.json();
      setTeams(teamsData.teams || []);

      // Load merch products
      const merchRes = await fetch(`${API_URL}/api/merch/admin?admin_key=${key}`);
      const merchData = await merchRes.json();
      setMerchProducts(merchData.products || []);

    } catch (error) {
      toast.error('Failed to load data');
    }
    setLoading(false);
  };

  // Merch functions
  const createMerchProduct = async (e) => {
    e.preventDefault();
    if (!newMerchName.trim() || !newMerchPrice) {
      toast.error('Name and price are required');
      return;
    }
    try {
      const response = await fetch(`${API_URL}/api/merch/admin?admin_key=${adminKey}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: newMerchName,
          price: parseFloat(newMerchPrice),
          image: newMerchImage,
          link: newMerchLink
        })
      });
      const data = await response.json();
      if (data.product) {
        setMerchProducts([data.product, ...merchProducts]);
        setNewMerchName('');
        setNewMerchPrice('');
        setNewMerchImage('');
        setNewMerchLink('');
        toast.success('Product created!');
      }
    } catch (error) {
      toast.error('Failed to create product');
    }
  };

  const deleteMerchProduct = async (productId) => {
    if (!window.confirm('Are you sure you want to delete this product?')) return;
    try {
      await fetch(`${API_URL}/api/merch/admin/${productId}?admin_key=${adminKey}`, {
        method: 'DELETE'
      });
      setMerchProducts(merchProducts.filter(p => p.product_id !== productId));
      toast.success('Product deleted');
    } catch (error) {
      toast.error('Failed to delete product');
    }
  };

  // Export functions
  const exportData = async (type) => {
    try {
      const response = await fetch(`${API_URL}/api/admin/export/${type}?admin_key=${adminKey}`);
      const data = await response.json();
      
      // Download as JSON
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `gwins_${type}_export_${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      toast.success(`${type} data exported!`);
    } catch (error) {
      toast.error(`Failed to export ${type}`);
    }
  };

  // Delete player
  const deletePlayer = async (playerId) => {
    if (!window.confirm('Are you sure you want to delete this player? This will also delete their posts and comments.')) return;
    try {
      await fetch(`${API_URL}/api/admin/players/${playerId}?admin_key=${adminKey}`, {
        method: 'DELETE'
      });
      setPlayers(players.filter(p => p.player_id !== playerId));
      toast.success('Player deleted');
    } catch (error) {
      toast.error('Failed to delete player');
    }
  };

  // Delete approved media
  const deleteApprovedMedia = async (mediaId) => {
    if (!window.confirm('Are you sure you want to delete this published media?')) return;
    try {
      await fetch(`${API_URL}/api/media/admin/${mediaId}?admin_key=${adminKey}`, {
        method: 'DELETE'
      });
      setApprovedMedia(approvedMedia.filter(m => m.media_id !== mediaId));
      toast.success('Media deleted');
    } catch (error) {
      toast.error('Failed to delete media');
    }
  };

  // Delete team
  const deleteTeam = async (teamId) => {
    if (!window.confirm('Are you sure you want to delete this team?')) return;
    try {
      await fetch(`${API_URL}/api/admin/teams/${teamId}?admin_key=${adminKey}`, {
        method: 'DELETE'
      });
      setTeams(teams.filter(t => t.team_id !== teamId));
      toast.success('Team deleted');
    } catch (error) {
      toast.error('Failed to delete team');
    }
  };

  // Edit player - open modal
  const openEditPlayer = (player) => {
    setEditFormData({
      name: player.name || '',
      nickname: player.nickname || '',
      location: player.location || '',
      experience_level: player.experience_level || 'Beginner',
      favorite_position: player.favorite_position || '',
      primary_marker: player.primary_marker || '',
      years_playing: player.years_playing || '',
      play_style: player.play_style || '',
      home_field: player.home_field || '',
      championships: player.championships || '',
      awards: player.awards || '',
      instagram_handle: player.instagram_handle || '',
      bio: player.bio || '',
      past_teams: player.past_teams || '',
      privacy: player.privacy || 'public',
    });
    setEditingPlayer(player);
  };

  // Save player edits
  const savePlayerEdits = async () => {
    if (!editingPlayer) return;
    try {
      const response = await fetch(`${API_URL}/api/admin/players/${editingPlayer.player_id}?admin_key=${adminKey}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(editFormData)
      });
      if (!response.ok) throw new Error('Failed to update');
      const updatedPlayer = { ...editingPlayer, ...editFormData };
      setPlayers(players.map(p => p.player_id === editingPlayer.player_id ? updatedPlayer : p));
      setEditingPlayer(null);
      setEditFormData({});
      toast.success('Player updated');
    } catch (error) {
      toast.error('Failed to update player');
    }
  };

  // Edit team - open modal
  const openEditTeam = (team) => {
    setEditFormData({
      name: team.name || '',
      location: team.location || '',
      division: team.division || '',
      league: team.league || '',
      bio: team.bio || team.description || '',
      home_field: team.home_field || '',
      team_logo: team.team_logo || team.logo || '',
      primary_color: team.primary_color || '',
      roster_size: team.roster_size || '',
      achievements: team.achievements || '',
      website: team.website || '',
      instagram_handle: team.instagram_handle || '',
      established_year: team.established_year || '',
    });
    setEditingTeam(team);
  };

  // Save team edits
  const saveTeamEdits = async () => {
    if (!editingTeam) return;
    try {
      const response = await fetch(`${API_URL}/api/admin/teams/${editingTeam.team_id}?admin_key=${adminKey}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(editFormData)
      });
      if (!response.ok) throw new Error('Failed to update');
      const updatedTeam = { ...editingTeam, ...editFormData };
      setTeams(teams.map(t => t.team_id === editingTeam.team_id ? updatedTeam : t));
      setEditingTeam(null);
      setEditFormData({});
      toast.success('Team updated');
    } catch (error) {
      toast.error('Failed to update team');
    }
  };

  // Edit media - open modal
  const openEditMedia = (media) => {
    setMediaEditData({
      caption: media.caption || '',
      team_name: media.team_name || '',
      is_sponsored: media.is_sponsored || false,
      tags: media.tags?.join(', ') || '',
    });
    setEditingMedia(media);
  };

  // Save media edits
  const saveMediaEdits = async () => {
    if (!editingMedia) return;
    try {
      const updateData = {
        ...mediaEditData,
        tags: mediaEditData.tags ? mediaEditData.tags.split(',').map(t => t.trim()).filter(t => t) : [],
      };
      
      const response = await fetch(`${API_URL}/api/media/admin/${editingMedia.media_id}?admin_key=${adminKey}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updateData)
      });
      if (!response.ok) throw new Error('Failed to update');
      
      const result = await response.json();
      setApprovedMedia(approvedMedia.map(m => 
        m.media_id === editingMedia.media_id ? result.media : m
      ));
      setEditingMedia(null);
      setMediaEditData({});
      toast.success('Media updated');
    } catch (error) {
      toast.error('Failed to update media');
    }
  };

  // Media actions
  const approveMedia = async (mediaId) => {
    try {
      await fetch(`${API_URL}/api/media/admin/approve/${mediaId}?admin_key=${adminKey}`, {
        method: 'POST'
      });
      setPendingMedia(pendingMedia.filter(m => m.media_id !== mediaId));
      toast.success('Media approved');
    } catch (error) {
      toast.error('Failed to approve media');
    }
  };

  const rejectMedia = async (mediaId) => {
    const reason = window.prompt('Rejection reason (optional):');
    try {
      await fetch(`${API_URL}/api/media/admin/reject/${mediaId}?admin_key=${adminKey}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason: reason || '' })
      });
      setPendingMedia(pendingMedia.filter(m => m.media_id !== mediaId));
      toast.success('Media rejected');
    } catch (error) {
      toast.error('Failed to reject media');
    }
  };

  const deleteMedia = async (mediaId) => {
    if (!window.confirm('Are you sure you want to delete this media?')) return;
    try {
      await fetch(`${API_URL}/api/media/admin/${mediaId}?admin_key=${adminKey}`, {
        method: 'DELETE'
      });
      setPendingMedia(pendingMedia.filter(m => m.media_id !== mediaId));
      toast.success('Media deleted');
    } catch (error) {
      toast.error('Failed to delete media');
    }
  };

  // Admin file upload handlers
  const handleAdminFileSelect = (e) => {
    const files = Array.from(e.target.files);
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'video/mp4', 'video/quicktime', 'video/webm', 'video/x-msvideo'];
    
    const validFiles = files.filter(file => {
      if (!allowedTypes.includes(file.type)) {
        toast.error(`${file.name}: Invalid file type`);
        return false;
      }
      return true;
    });
    
    setAdminUploadFiles(prev => [...prev, ...validFiles]);
    
    // Generate previews
    validFiles.forEach(file => {
      const reader = new FileReader();
      reader.onload = (e) => {
        setAdminUploadPreviews(prev => [...prev, {
          name: file.name,
          url: e.target.result,
          type: file.type.startsWith('video/') ? 'video' : 'image',
          size: file.size
        }]);
      };
      reader.readAsDataURL(file);
    });
  };

  const removeAdminFile = (index) => {
    setAdminUploadFiles(prev => prev.filter((_, i) => i !== index));
    setAdminUploadPreviews(prev => prev.filter((_, i) => i !== index));
  };

  const handleAdminUpload = async (e) => {
    e.preventDefault();
    if (adminUploadFiles.length === 0) {
      toast.error('Please select files to upload');
      return;
    }
    
    setAdminUploading(true);
    
    try {
      const formData = new FormData();
      formData.append('admin_key', adminKey);
      formData.append('caption', adminUploadCaption);
      formData.append('team_name', adminUploadTeam);
      formData.append('is_sponsored', adminUploadSponsored);
      
      adminUploadFiles.forEach(file => {
        formData.append('files', file);
      });
      
      const response = await fetch(`${API_URL}/api/uploads/media/admin`, {
        method: 'POST',
        body: formData
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Upload failed');
      }
      
      const data = await response.json();
      toast.success(`${data.total_uploaded} file(s) uploaded and published!`);
      
      // Reset form
      setAdminUploadFiles([]);
      setAdminUploadPreviews([]);
      setAdminUploadCaption('');
      setAdminUploadTeam('');
      setAdminUploadSponsored(false);
      
      // Refresh data
      loadDashboardData();
      
    } catch (error) {
      toast.error(error.message || 'Failed to upload');
    } finally {
      setAdminUploading(false);
    }
  };

  // Post actions
  const deletePost = async (postId) => {
    if (!window.confirm('Are you sure you want to delete this post?')) return;
    try {
      await fetch(`${API_URL}/api/admin/posts/${postId}?admin_key=${adminKey}`, {
        method: 'DELETE'
      });
      setPosts(posts.filter(p => p.post_id !== postId));
      toast.success('Post deleted');
    } catch (error) {
      toast.error('Failed to delete post');
    }
  };

  const hidePost = async (postId, hidden) => {
    try {
      const action = hidden ? 'unhide' : 'hide';
      await fetch(`${API_URL}/api/admin/posts/${postId}/${action}?admin_key=${adminKey}`, {
        method: 'POST'
      });
      setPosts(posts.map(p => p.post_id === postId ? {...p, hidden: !hidden} : p));
      toast.success(`Post ${action}d`);
    } catch (error) {
      toast.error('Failed to update post');
    }
  };

  // Comment actions
  const deleteComment = async (commentId) => {
    if (!window.confirm('Are you sure you want to delete this comment?')) return;
    try {
      await fetch(`${API_URL}/api/admin/comments/${commentId}?admin_key=${adminKey}`, {
        method: 'DELETE'
      });
      setComments(comments.filter(c => c.comment_id !== commentId));
      toast.success('Comment deleted');
    } catch (error) {
      toast.error('Failed to delete comment');
    }
  };

  // News actions
  const createNews = async (e) => {
    e.preventDefault();
    if (!newNewsTitle.trim()) {
      toast.error('Please enter a news title');
      return;
    }
    try {
      const response = await fetch(`${API_URL}/api/admin/news?admin_key=${adminKey}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ title: newNewsTitle, link: newNewsLink || '#' })
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to create news');
      }
      
      const data = await response.json();
      setNews([data.news, ...news]);
      setNewNewsTitle('');
      setNewNewsLink('');
      toast.success('News created successfully!');
    } catch (error) {
      console.error('Create news error:', error);
      toast.error(error.message || 'Failed to create news');
    }
  };

  const toggleNewsActive = async (newsId, currentActive) => {
    try {
      await fetch(`${API_URL}/api/admin/news/${newsId}?admin_key=${adminKey}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ active: !currentActive })
      });
      setNews(news.map(n => n.news_id === newsId ? {...n, active: !currentActive} : n));
      toast.success('News updated');
    } catch (error) {
      toast.error('Failed to update news');
    }
  };

  const deleteNews = async (newsId) => {
    if (!window.confirm('Are you sure you want to delete this news item?')) return;
    try {
      await fetch(`${API_URL}/api/admin/news/${newsId}?admin_key=${adminKey}`, {
        method: 'DELETE'
      });
      setNews(news.filter(n => n.news_id !== newsId));
      toast.success('News deleted');
    } catch (error) {
      toast.error('Failed to delete news');
    }
  };

  // Referral actions
  const approveReferral = async (applicationId) => {
    try {
      const response = await fetch(`${API_URL}/api/referrals/admin/approve/${applicationId}`, {
        method: 'POST'
      });
      const data = await response.json();
      setReferralApplications(referralApplications.filter(a => a.application_id !== applicationId));
      toast.success(`Approved! Code: ${data.referral_code}`);
      loadDashboardData();
    } catch (error) {
      toast.error('Failed to approve');
    }
  };

  const rejectReferral = async (applicationId) => {
    const reason = window.prompt('Rejection reason (optional):');
    try {
      await fetch(`${API_URL}/api/referrals/admin/reject/${applicationId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason: reason || '' })
      });
      setReferralApplications(referralApplications.filter(a => a.application_id !== applicationId));
      toast.success('Application rejected');
    } catch (error) {
      toast.error('Failed to reject');
    }
  };

  // Login screen
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-[#0a0b0c] flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className="bg-[#131416] border border-white/10 rounded-2xl p-8">
            <div className="flex items-center justify-center mb-6">
              <Shield className="w-12 h-12 text-[#00d4aa]" />
            </div>
            <h1 className="text-2xl font-bold text-white text-center mb-2">Admin Dashboard</h1>
            <p className="text-white/60 text-center mb-8">Enter your admin key to continue</p>
            
            <form onSubmit={handleLogin} className="space-y-4">
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  value={adminKey}
                  onChange={(e) => setAdminKey(e.target.value)}
                  placeholder="Admin Key"
                  className="w-full px-4 py-3 pr-12 bg-[#1a1b1e] border border-white/10 rounded-lg text-white placeholder-white/40 focus:outline-none focus:border-[#00d4aa]"
                  data-testid="admin-key-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white/60"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
              <Button 
                type="submit" 
                className="w-full bg-[#00d4aa] hover:bg-[#33e0bb] text-[#0a0b0c] font-bold py-3"
                disabled={loading}
                data-testid="admin-login-btn"
              >
                {loading ? 'Verifying...' : 'Access Dashboard'}
              </Button>
            </form>
            
            <a href="/" className="flex items-center justify-center gap-2 mt-6 text-white/40 hover:text-white/60">
              <ChevronLeft className="w-4 h-4" />
              Back to site
            </a>
          </div>
        </div>
      </div>
    );
  }

  // Dashboard
  return (
    <div className="min-h-screen bg-[#0a0b0c]">
      {/* Header */}
      <header className="bg-[#131416] border-b border-white/10 px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Shield className="w-8 h-8 text-[#00d4aa]" />
            <h1 className="text-xl font-bold text-white">GWINS Admin</h1>
          </div>
          <div className="flex items-center gap-4">
            <Button 
              onClick={() => loadDashboardData()} 
              variant="ghost" 
              className="text-white/60 hover:text-white"
              data-testid="refresh-btn"
            >
              <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
            </Button>
            <Button 
              onClick={handleLogout} 
              variant="ghost" 
              className="text-white/60 hover:text-red-400"
              data-testid="logout-btn"
            >
              <LogOut className="w-5 h-5" />
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto p-6">
        {/* Tabs */}
        <div className="flex flex-wrap gap-2 mb-8">
          {[
            { id: 'overview', icon: BarChart3, label: 'Overview' },
            { id: 'users', icon: Users, label: `Users (${players.length + teams.length})` },
            { id: 'media', icon: Image, label: `Media${pendingMedia.length > 0 ? ` (${pendingMedia.length})` : ''}` },
            { id: 'posts', icon: FileText, label: 'Posts' },
            { id: 'comments', icon: MessageSquare, label: 'Comments' },
            { id: 'news', icon: Newspaper, label: 'News' },
            { id: 'referrals', icon: Users, label: 'Referrals' },
            { id: 'accounts', icon: DollarSign, label: 'Accounts' },
            { id: 'merch', icon: ShoppingBag, label: `Merch (${merchProducts.length})` },
          ].map(tab => (
            <Button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              variant={activeTab === tab.id ? 'default' : 'ghost'}
              className={activeTab === tab.id 
                ? 'bg-[#00d4aa] text-[#0a0b0c]' 
                : 'text-white/60 hover:text-white hover:bg-white/5'}
              data-testid={`tab-${tab.id}`}
            >
              <tab.icon className="w-4 h-4 mr-2" />
              {tab.label}
            </Button>
          ))}
        </div>

        {/* Users Tab */}
        {activeTab === 'users' && (
          <div className="space-y-6">
            {/* Export Buttons */}
            <div className="bg-[#131416] border border-white/10 rounded-xl p-4">
              <h3 className="text-lg font-semibold text-white mb-4">Export Data</h3>
              <div className="flex flex-wrap gap-3">
                <Button onClick={() => exportData('players')} variant="outline" className="border-white/20 text-white hover:bg-white/10">
                  Export Players (JSON)
                </Button>
                <Button onClick={() => exportData('teams')} variant="outline" className="border-white/20 text-white hover:bg-white/10">
                  Export Teams (JSON)
                </Button>
                <Button onClick={() => exportData('subscribers')} variant="outline" className="border-white/20 text-white hover:bg-white/10">
                  Export Subscribers (JSON)
                </Button>
                <Button onClick={() => exportData('referrals')} variant="outline" className="border-white/20 text-white hover:bg-white/10">
                  Export Referrals (JSON)
                </Button>
              </div>
            </div>

            {/* Players List */}
            <div className="bg-[#131416] border border-white/10 rounded-xl overflow-hidden">
              <div className="p-4 border-b border-white/10">
                <h3 className="text-lg font-semibold text-white">Player Profiles ({players.length})</h3>
                <p className="text-white/60 text-sm">Manage community player profiles</p>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-[#1a1b1e]">
                    <tr>
                      <th className="text-left text-white/60 text-sm font-medium px-4 py-3">Name</th>
                      <th className="text-left text-white/60 text-sm font-medium px-4 py-3">Email</th>
                      <th className="text-left text-white/60 text-sm font-medium px-4 py-3">Location</th>
                      <th className="text-left text-white/60 text-sm font-medium px-4 py-3">Level</th>
                      <th className="text-left text-white/60 text-sm font-medium px-4 py-3">Created</th>
                      <th className="text-right text-white/60 text-sm font-medium px-4 py-3">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/5">
                    {players.map(player => (
                      <tr key={player.player_id} className="hover:bg-white/5">
                        <td className="px-4 py-3">
                          <p className="text-white font-medium">{player.name}</p>
                          {player.nickname && <p className="text-white/40 text-sm">@{player.nickname}</p>}
                        </td>
                        <td className="px-4 py-3 text-white/60">{player.recovery_email || '-'}</td>
                        <td className="px-4 py-3 text-white/60">{player.location || '-'}</td>
                        <td className="px-4 py-3 text-white/60">{player.experience_level || '-'}</td>
                        <td className="px-4 py-3 text-white/40 text-sm">
                          {player.created_at ? new Date(player.created_at).toLocaleDateString() : '-'}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <div className="flex items-center justify-end gap-2">
                            <Button
                              size="sm"
                              variant="ghost"
                              className="text-white/60 hover:text-[#00d4aa]"
                              onClick={() => openEditPlayer(player)}
                              data-testid={`edit-player-${player.player_id}`}
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="text-white/60 hover:text-red-400"
                              onClick={() => deletePlayer(player.player_id)}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {players.length === 0 && (
                  <p className="text-white/40 text-center py-8">No player profiles yet</p>
                )}
              </div>
            </div>

            {/* Teams List */}
            <div className="bg-[#131416] border border-white/10 rounded-xl overflow-hidden">
              <div className="p-4 border-b border-white/10">
                <h3 className="text-lg font-semibold text-white">Teams ({teams.length})</h3>
                <p className="text-white/60 text-sm">Manage community teams</p>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-[#1a1b1e]">
                    <tr>
                      <th className="text-left text-white/60 text-sm font-medium px-4 py-3">Team Name</th>
                      <th className="text-left text-white/60 text-sm font-medium px-4 py-3">Location</th>
                      <th className="text-left text-white/60 text-sm font-medium px-4 py-3">Members</th>
                      <th className="text-left text-white/60 text-sm font-medium px-4 py-3">Created</th>
                      <th className="text-right text-white/60 text-sm font-medium px-4 py-3">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/5">
                    {teams.map(team => (
                      <tr key={team.team_id} className="hover:bg-white/5">
                        <td className="px-4 py-3">
                          <p className="text-white font-medium">{team.name}</p>
                        </td>
                        <td className="px-4 py-3 text-white/60">{team.location || '-'}</td>
                        <td className="px-4 py-3 text-white/60">{team.member_count || 0}</td>
                        <td className="px-4 py-3 text-white/40 text-sm">
                          {team.created_at ? new Date(team.created_at).toLocaleDateString() : '-'}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <div className="flex items-center justify-end gap-2">
                            <Button
                              size="sm"
                              variant="ghost"
                              className="text-white/60 hover:text-[#00d4aa]"
                              onClick={() => openEditTeam(team)}
                              data-testid={`edit-team-${team.team_id}`}
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="text-white/60 hover:text-red-400"
                              onClick={() => deleteTeam(team.team_id)}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {teams.length === 0 && (
                  <p className="text-white/40 text-center py-8">No teams yet</p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Media Tab */}
        {activeTab === 'media' && (
          <div className="space-y-6">
            {/* Admin Upload Section */}
            <div className="bg-[#131416] border border-white/10 rounded-xl overflow-hidden">
              <div className="p-4 border-b border-white/10">
                <h3 className="text-lg font-semibold text-white">Admin Upload (No Restrictions)</h3>
                <p className="text-white/60 text-sm">Upload media directly - bypasses approval, published immediately</p>
              </div>
              <div className="p-4">
                <form onSubmit={handleAdminUpload} className="space-y-4">
                  {/* File Input */}
                  <input
                    ref={adminFileInputRef}
                    type="file"
                    multiple
                    accept="image/jpeg,image/png,image/gif,image/webp,video/mp4,video/quicktime,video/webm,video/x-msvideo"
                    onChange={handleAdminFileSelect}
                    className="hidden"
                  />
                  <div 
                    onClick={() => adminFileInputRef.current?.click()}
                    className="w-full p-6 border-2 border-dashed border-white/20 rounded-lg text-center cursor-pointer hover:border-[#00d4aa]/50 transition-colors"
                  >
                    <Upload className="w-8 h-8 text-white/40 mx-auto mb-2" />
                    <p className="text-white/60 mb-1">Click to select files</p>
                    <p className="text-white/40 text-sm">No size restrictions for admin uploads</p>
                  </div>

                  {/* File Previews */}
                  {adminUploadPreviews.length > 0 && (
                    <div className="grid grid-cols-4 sm:grid-cols-6 gap-3">
                      {adminUploadPreviews.map((preview, index) => (
                        <div key={index} className="relative group">
                          <div className="aspect-square rounded-lg overflow-hidden bg-[#1a1b1e]">
                            {preview.type === 'video' ? (
                              <div className="w-full h-full flex items-center justify-center">
                                <FileVideo className="w-6 h-6 text-white/40" />
                              </div>
                            ) : (
                              <img src={preview.url} alt={preview.name} className="w-full h-full object-cover" />
                            )}
                          </div>
                          <button
                            type="button"
                            onClick={() => removeAdminFile(index)}
                            className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            <X className="w-3 h-3 text-white" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Options */}
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <label className="block text-white/60 text-sm mb-1">Team Name</label>
                      <input
                        type="text"
                        value={adminUploadTeam}
                        onChange={(e) => setAdminUploadTeam(e.target.value)}
                        placeholder="GWINS Official"
                        className="w-full px-3 py-2 bg-[#1a1b1e] border border-white/10 rounded-lg text-white placeholder-white/40 focus:outline-none focus:border-[#00d4aa] text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-white/60 text-sm mb-1">Caption</label>
                      <input
                        type="text"
                        value={adminUploadCaption}
                        onChange={(e) => setAdminUploadCaption(e.target.value)}
                        placeholder="Optional caption..."
                        className="w-full px-3 py-2 bg-[#1a1b1e] border border-white/10 rounded-lg text-white placeholder-white/40 focus:outline-none focus:border-[#00d4aa] text-sm"
                      />
                    </div>
                    <div className="flex items-end">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={adminUploadSponsored}
                          onChange={(e) => setAdminUploadSponsored(e.target.checked)}
                          className="w-4 h-4 rounded border-white/20 bg-[#1a1b1e] text-[#00d4aa] focus:ring-[#00d4aa]"
                        />
                        <span className="text-white/60 text-sm">Mark as Sponsored</span>
                      </label>
                    </div>
                  </div>

                  <Button
                    type="submit"
                    className="bg-[#00d4aa] hover:bg-[#33e0bb] text-[#0a0b0c] font-bold"
                    disabled={adminUploading || adminUploadFiles.length === 0}
                  >
                    {adminUploading ? (
                      <span className="flex items-center">
                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Uploading...
                      </span>
                    ) : (
                      <>
                        <Upload className="w-4 h-4 mr-2" />
                        Upload & Publish ({adminUploadFiles.length} files)
                      </>
                    )}
                  </Button>
                </form>
              </div>
            </div>

            {/* Pending Media Section */}
            <div className="bg-[#131416] border border-white/10 rounded-xl overflow-hidden">
              <div className="p-4 border-b border-white/10">
                <h3 className="text-lg font-semibold text-white">Pending Media Submissions ({pendingMedia.length})</h3>
                <p className="text-white/60 text-sm">Review and approve user-submitted photos and videos</p>
              </div>
              <div className="divide-y divide-white/5">
                {pendingMedia.map(media => (
                  <div key={media.media_id} className="p-4">
                    <div className="flex gap-4">
                      {/* Media Preview */}
                      <div className="w-32 h-24 rounded-lg overflow-hidden bg-[#1a1b1e] flex-shrink-0">
                        {media.type === 'video' ? (
                          <div className="w-full h-full flex items-center justify-center text-white/40">
                            <span className="text-xs">Video</span>
                          </div>
                        ) : (
                          <img 
                            src={media.url.startsWith('/api') ? `${API_URL}${media.url}` : media.url}
                            alt={media.caption || 'Uploaded media'}
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              e.target.style.display = 'none';
                              e.target.parentElement.innerHTML = '<span class="text-white/40 text-xs p-2">Failed to load</span>';
                            }}
                          />
                        )}
                      </div>
                      
                      {/* Media Info */}
                      <div className="flex-1 min-w-0">
                        <div className="grid grid-cols-2 gap-2 mb-2">
                          <div>
                            <p className="text-white/40 text-xs">Submitted by</p>
                            <p className="text-white font-medium">{media.uploader_name}</p>
                            <p className="text-white/60 text-sm">{media.uploader_email}</p>
                          </div>
                        <div>
                          <p className="text-white/40 text-xs">Team</p>
                          <p className="text-white">{media.team_name || 'Community'}</p>
                        </div>
                      </div>
                      {media.caption && (
                        <div className="mb-2">
                          <p className="text-white/40 text-xs">Caption</p>
                          <p className="text-white/80 text-sm">{media.caption}</p>
                        </div>
                      )}
                      <div>
                        <p className="text-white/40 text-xs">URL</p>
                        <a 
                          href={media.url} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-[#00d4aa] text-sm hover:underline truncate block"
                        >
                          {media.url}
                        </a>
                      </div>
                    </div>
                    
                    {/* Actions */}
                    <div className="flex flex-col gap-2">
                      <Button 
                        size="sm"
                        variant="outline"
                        className="border-white/30 text-white hover:bg-white/10"
                        onClick={() => {
                          const url = (media.url || media.file_path)?.startsWith('/api') 
                            ? `${API_URL}${media.url || media.file_path}` 
                            : (media.url || media.file_path);
                          window.open(url, '_blank');
                        }}
                      >
                        <Download className="w-4 h-4 mr-1" /> Download
                      </Button>
                      <Button 
                        size="sm" 
                        className="bg-green-600 hover:bg-green-700"
                        onClick={() => approveMedia(media.media_id)}
                        data-testid={`approve-media-${media.media_id}`}
                      >
                        <Check className="w-4 h-4 mr-1" /> Approve
                      </Button>
                      <Button 
                        size="sm" 
                        variant="destructive"
                        onClick={() => rejectMedia(media.media_id)}
                        data-testid={`reject-media-${media.media_id}`}
                      >
                        <X className="w-4 h-4 mr-1" /> Reject
                      </Button>
                      <Button 
                        size="sm" 
                        variant="ghost"
                        className="text-white/60 hover:text-red-400"
                        onClick={() => deleteMedia(media.media_id)}
                        data-testid={`delete-media-${media.media_id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
              {pendingMedia.length === 0 && (
                <p className="text-white/40 text-center py-8">No pending media submissions</p>
              )}
            </div>
          </div>

            {/* Published Media Section */}
            <div className="bg-[#131416] border border-white/10 rounded-xl overflow-hidden">
              <div className="p-4 border-b border-white/10">
                <h3 className="text-lg font-semibold text-white">Published Media ({approvedMedia.length})</h3>
                <p className="text-white/60 text-sm">Manage approved and published media in the gallery</p>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 p-4">
                {approvedMedia.map(media => (
                  <div key={media.media_id} className="relative group">
                    <div className="aspect-square rounded-lg overflow-hidden bg-[#1a1b1e]">
                      {media.type === 'video' ? (
                        <div className="w-full h-full flex items-center justify-center">
                          <FileVideo className="w-8 h-8 text-white/40" />
                        </div>
                      ) : (
                        <img 
                          src={(media.url || media.file_path)?.startsWith('/api') ? `${API_URL}${media.url || media.file_path}` : (media.url || media.file_path)}
                          alt={media.caption || 'Media'}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            e.target.src = 'https://via.placeholder.com/200x200?text=Error';
                          }}
                        />
                      )}
                    </div>
                    {/* Sponsored badge */}
                    {media.is_sponsored && (
                      <div className="absolute top-2 left-2 bg-[#00d4aa] text-[#0a0b0c] text-xs font-bold px-2 py-0.5 rounded">
                        SPONSORED
                      </div>
                    )}
                    {/* Action overlay */}
                    <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2 rounded-lg">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          const url = (media.url || media.file_path)?.startsWith('/api') 
                            ? `${API_URL}${media.url || media.file_path}` 
                            : (media.url || media.file_path);
                          window.open(url, '_blank');
                        }}
                        className="border-white/30 text-white hover:bg-white/10"
                        title="Download"
                      >
                        <Download className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => openEditMedia(media)}
                        className="border-white/30 text-white hover:bg-white/10"
                        title="Edit"
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="destructive"
                        onClick={() => deleteApprovedMedia(media.media_id)}
                        className="bg-red-600 hover:bg-red-700"
                        title="Delete"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                    {/* Info */}
                    <div className="mt-2">
                      <p className="text-white/60 text-xs truncate">{media.team_name || media.uploader_name || 'Unknown'}</p>
                      {media.caption && <p className="text-white/40 text-xs truncate">{media.caption}</p>}
                    </div>
                  </div>
                ))}
              </div>
              {approvedMedia.length === 0 && (
                <p className="text-white/40 text-center py-8">No published media yet</p>
              )}
            </div>
          </div>
        )}

        {/* Overview Tab */}
        {activeTab === 'overview' && stats && (
          <div className="space-y-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[
                { label: 'Total Posts', value: stats.stats.total_posts, color: 'text-blue-400' },
                { label: 'Total Comments', value: stats.stats.total_comments, color: 'text-purple-400' },
                { label: 'Players', value: stats.stats.total_players, color: 'text-green-400' },
                { label: 'Teams', value: stats.stats.total_teams, color: 'text-yellow-400' },
                { label: 'Pending Referrals', value: stats.stats.pending_referrals, color: 'text-orange-400' },
                { label: 'Active Referrers', value: stats.stats.approved_referrals, color: 'text-teal-400' },
                { label: 'Subscribers', value: stats.stats.total_subscribers, color: 'text-pink-400' },
                { label: 'Active News', value: stats.stats.total_news, color: 'text-cyan-400' },
              ].map((stat, i) => (
                <div key={i} className="bg-[#131416] border border-white/10 rounded-xl p-4">
                  <p className="text-white/60 text-sm">{stat.label}</p>
                  <p className={`text-3xl font-bold ${stat.color}`}>{stat.value}</p>
                </div>
              ))}
            </div>

            {/* Recent Activity */}
            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-[#131416] border border-white/10 rounded-xl p-6">
                <h3 className="text-lg font-semibold text-white mb-4">Recent Posts</h3>
                <div className="space-y-3">
                  {stats.recent_posts?.map((post, i) => (
                    <div key={i} className="flex items-start gap-3 p-3 bg-[#1a1b1e] rounded-lg">
                      <div className="flex-1 min-w-0">
                        <p className="text-white font-medium truncate">{post.author_name}</p>
                        <p className="text-white/60 text-sm truncate">{post.content}</p>
                      </div>
                    </div>
                  ))}
                  {(!stats.recent_posts || stats.recent_posts.length === 0) && (
                    <p className="text-white/40 text-center py-4">No recent posts</p>
                  )}
                </div>
              </div>

              <div className="bg-[#131416] border border-white/10 rounded-xl p-6">
                <h3 className="text-lg font-semibold text-white mb-4">Pending Referral Applications</h3>
                <div className="space-y-3">
                  {stats.recent_applications?.map((app, i) => (
                    <div key={i} className="flex items-center justify-between p-3 bg-[#1a1b1e] rounded-lg">
                      <div>
                        <p className="text-white font-medium">{app.name}</p>
                        <p className="text-white/60 text-sm">{app.email}</p>
                      </div>
                      <div className="flex gap-2">
                        <Button 
                          size="sm" 
                          className="bg-green-600 hover:bg-green-700"
                          onClick={() => approveReferral(app.application_id)}
                        >
                          <Check className="w-4 h-4" />
                        </Button>
                        <Button 
                          size="sm" 
                          variant="destructive"
                          onClick={() => rejectReferral(app.application_id)}
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                  {(!stats.recent_applications || stats.recent_applications.length === 0) && (
                    <p className="text-white/40 text-center py-4">No pending applications</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Posts Tab */}
        {activeTab === 'posts' && (
          <div className="bg-[#131416] border border-white/10 rounded-xl overflow-hidden">
            <div className="p-4 border-b border-white/10">
              <h3 className="text-lg font-semibold text-white">Community Posts ({posts.length})</h3>
              <p className="text-white/60 text-sm">Manage and moderate community posts</p>
            </div>
            <div className="divide-y divide-white/5">
              {posts.map(post => (
                <div key={post.post_id} className={`p-4 ${post.hidden ? 'opacity-50 bg-red-500/5' : ''}`}>
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-white font-medium">{post.author_name}</span>
                        {post.hidden && (
                          <span className="px-2 py-0.5 bg-red-500/20 text-red-400 text-xs rounded">Hidden</span>
                        )}
                      </div>
                      <p className="text-white/80">{post.content}</p>
                      <p className="text-white/40 text-xs mt-1">
                        {new Date(post.created_at).toLocaleString()} • {post.like_count || 0} likes • {post.comment_count || 0} comments
                      </p>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-white/60 hover:text-yellow-400"
                        onClick={() => hidePost(post.post_id, post.hidden)}
                        data-testid={`hide-post-${post.post_id}`}
                      >
                        {post.hidden ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-white/60 hover:text-red-400"
                        onClick={() => deletePost(post.post_id)}
                        data-testid={`delete-post-${post.post_id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
              {posts.length === 0 && (
                <p className="text-white/40 text-center py-8">No posts yet</p>
              )}
            </div>
          </div>
        )}

        {/* Comments Tab */}
        {activeTab === 'comments' && (
          <div className="bg-[#131416] border border-white/10 rounded-xl overflow-hidden">
            <div className="p-4 border-b border-white/10">
              <h3 className="text-lg font-semibold text-white">Comments ({comments.length})</h3>
              <p className="text-white/60 text-sm">Moderate community comments</p>
            </div>
            <div className="divide-y divide-white/5">
              {comments.map(comment => (
                <div key={comment.comment_id} className="p-4">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <span className="text-white font-medium">{comment.author_name}</span>
                      <p className="text-white/80">{comment.content}</p>
                      <p className="text-white/40 text-xs mt-1">
                        {new Date(comment.created_at).toLocaleString()} • Post: {comment.post_id}
                      </p>
                    </div>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="text-white/60 hover:text-red-400"
                      onClick={() => deleteComment(comment.comment_id)}
                      data-testid={`delete-comment-${comment.comment_id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
              {comments.length === 0 && (
                <p className="text-white/40 text-center py-8">No comments yet</p>
              )}
            </div>
          </div>
        )}

        {/* News Tab */}
        {activeTab === 'news' && (
          <div className="space-y-6">
            {/* Add News Form */}
            <div className="bg-[#131416] border border-white/10 rounded-xl p-6">
              <h3 className="text-lg font-semibold text-white mb-4">Add News Item</h3>
              <form onSubmit={createNews} className="flex flex-col sm:flex-row gap-4">
                <input
                  type="text"
                  value={newNewsTitle}
                  onChange={(e) => setNewNewsTitle(e.target.value)}
                  placeholder="News title..."
                  className="flex-1 px-4 py-2 bg-[#1a1b1e] border border-white/10 rounded-lg text-white placeholder-white/40 focus:outline-none focus:border-[#00d4aa]"
                  data-testid="news-title-input"
                />
                <input
                  type="text"
                  value={newNewsLink}
                  onChange={(e) => setNewNewsLink(e.target.value)}
                  placeholder="Link (optional)"
                  className="sm:w-48 px-4 py-2 bg-[#1a1b1e] border border-white/10 rounded-lg text-white placeholder-white/40 focus:outline-none focus:border-[#00d4aa]"
                  data-testid="news-link-input"
                />
                <Button type="submit" className="bg-[#00d4aa] hover:bg-[#33e0bb] text-[#0a0b0c]" data-testid="add-news-btn">
                  Add News
                </Button>
              </form>
            </div>

            {/* News List */}
            <div className="bg-[#131416] border border-white/10 rounded-xl overflow-hidden">
              <div className="p-4 border-b border-white/10">
                <h3 className="text-lg font-semibold text-white">News Ticker Items ({news.length})</h3>
              </div>
              <div className="divide-y divide-white/5">
                {news.map(item => (
                  <div key={item.news_id} className={`p-4 ${!item.active ? 'opacity-50' : ''}`}>
                    <div className="flex items-center justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <p className="text-white font-medium">{item.title}</p>
                        <p className="text-white/40 text-sm">{item.link}</p>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="ghost"
                          className={item.active ? 'text-green-400' : 'text-white/40'}
                          onClick={() => toggleNewsActive(item.news_id, item.active)}
                          data-testid={`toggle-news-${item.news_id}`}
                        >
                          {item.active ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-white/60 hover:text-red-400"
                          onClick={() => deleteNews(item.news_id)}
                          data-testid={`delete-news-${item.news_id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
                {news.length === 0 && (
                  <p className="text-white/40 text-center py-8">No news items</p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Referrals Tab */}
        {activeTab === 'referrals' && (
          <div className="bg-[#131416] border border-white/10 rounded-xl overflow-hidden">
            <div className="p-4 border-b border-white/10">
              <h3 className="text-lg font-semibold text-white">Pending Applications ({referralApplications.length})</h3>
              <p className="text-white/60 text-sm">Review and approve referral program applications</p>
            </div>
            <div className="divide-y divide-white/5">
              {referralApplications.map(app => (
                <div key={app.application_id} className="p-4">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="grid grid-cols-2 gap-4 mb-2">
                        <div>
                          <p className="text-white/40 text-xs">Name</p>
                          <p className="text-white font-medium">{app.name}</p>
                        </div>
                        <div>
                          <p className="text-white/40 text-xs">Email</p>
                          <p className="text-white">{app.email}</p>
                        </div>
                        {app.phone && (
                          <div>
                            <p className="text-white/40 text-xs">Phone</p>
                            <p className="text-white">{app.phone}</p>
                          </div>
                        )}
                        {app.team_name && (
                          <div>
                            <p className="text-white/40 text-xs">Team</p>
                            <p className="text-white">{app.team_name}</p>
                          </div>
                        )}
                      </div>
                      {app.social_media && (
                        <div className="mb-2">
                          <p className="text-white/40 text-xs">Social Media</p>
                          <p className="text-white">{app.social_media}</p>
                        </div>
                      )}
                      {app.reason && (
                        <div className="mb-2">
                          <p className="text-white/40 text-xs">Reason</p>
                          <p className="text-white/80 text-sm">{app.reason}</p>
                        </div>
                      )}
                      {app.how_promote && (
                        <div>
                          <p className="text-white/40 text-xs">Promotion Plan</p>
                          <p className="text-white/80 text-sm">{app.how_promote}</p>
                        </div>
                      )}
                    </div>
                    <div className="flex flex-col gap-2">
                      <Button 
                        size="sm" 
                        className="bg-green-600 hover:bg-green-700"
                        onClick={() => approveReferral(app.application_id)}
                        data-testid={`approve-${app.application_id}`}
                      >
                        <Check className="w-4 h-4 mr-1" /> Approve
                      </Button>
                      <Button 
                        size="sm" 
                        variant="destructive"
                        onClick={() => rejectReferral(app.application_id)}
                        data-testid={`reject-${app.application_id}`}
                      >
                        <X className="w-4 h-4 mr-1" /> Reject
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
              {referralApplications.length === 0 && (
                <p className="text-white/40 text-center py-8">No pending applications</p>
              )}
            </div>
          </div>
        )}

        {/* Accounts Tab */}
        {activeTab === 'accounts' && (
          <div className="bg-[#131416] border border-white/10 rounded-xl overflow-hidden">
            <div className="p-4 border-b border-white/10">
              <h3 className="text-lg font-semibold text-white">Referral Accounts ({referralAccounts.length})</h3>
              <p className="text-white/60 text-sm">Active referrers and their performance</p>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-[#1a1b1e]">
                  <tr>
                    <th className="text-left text-white/60 text-sm font-medium px-4 py-3">Name</th>
                    <th className="text-left text-white/60 text-sm font-medium px-4 py-3">Code</th>
                    <th className="text-right text-white/60 text-sm font-medium px-4 py-3">Clicks</th>
                    <th className="text-right text-white/60 text-sm font-medium px-4 py-3">Orders</th>
                    <th className="text-right text-white/60 text-sm font-medium px-4 py-3">Sales</th>
                    <th className="text-right text-white/60 text-sm font-medium px-4 py-3">Commission</th>
                    <th className="text-right text-white/60 text-sm font-medium px-4 py-3">Pending</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/5">
                  {referralAccounts.map(account => (
                    <tr key={account.referrer_id} className="hover:bg-white/5">
                      <td className="px-4 py-3">
                        <p className="text-white font-medium">{account.name}</p>
                        <p className="text-white/40 text-sm">{account.email}</p>
                      </td>
                      <td className="px-4 py-3">
                        <code className="text-[#00d4aa] bg-[#00d4aa]/10 px-2 py-1 rounded">{account.referral_code}</code>
                      </td>
                      <td className="px-4 py-3 text-right text-white">{account.total_clicks || 0}</td>
                      <td className="px-4 py-3 text-right text-white">{account.total_orders || 0}</td>
                      <td className="px-4 py-3 text-right text-white">${(account.total_sales || 0).toFixed(2)}</td>
                      <td className="px-4 py-3 text-right text-green-400">${(account.total_commission || 0).toFixed(2)}</td>
                      <td className="px-4 py-3 text-right text-yellow-400">${(account.pending_payout || 0).toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {referralAccounts.length === 0 && (
                <p className="text-white/40 text-center py-8">No referral accounts yet</p>
              )}
            </div>
          </div>
        )}

        {/* Merch Tab */}
        {activeTab === 'merch' && (
          <div className="space-y-6">
            {/* Add Merch Form */}
            <div className="bg-[#131416] border border-white/10 rounded-xl p-6">
              <h3 className="text-lg font-semibold text-white mb-4">Add New Merch Product</h3>
              <form onSubmit={createMerchProduct} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
                <div>
                  <label className="block text-white/60 text-sm mb-1">Product Name *</label>
                  <input
                    type="text"
                    value={newMerchName}
                    onChange={(e) => setNewMerchName(e.target.value)}
                    placeholder="Product name"
                    className="w-full px-3 py-2 bg-[#1a1b1e] border border-white/10 rounded-lg text-white placeholder-white/40 focus:outline-none focus:border-[#00d4aa] text-sm"
                    required
                  />
                </div>
                <div>
                  <label className="block text-white/60 text-sm mb-1">Price *</label>
                  <input
                    type="number"
                    step="0.01"
                    value={newMerchPrice}
                    onChange={(e) => setNewMerchPrice(e.target.value)}
                    placeholder="29.99"
                    className="w-full px-3 py-2 bg-[#1a1b1e] border border-white/10 rounded-lg text-white placeholder-white/40 focus:outline-none focus:border-[#00d4aa] text-sm"
                    required
                  />
                </div>
                <div>
                  <label className="block text-white/60 text-sm mb-1">Image URL</label>
                  <input
                    type="url"
                    value={newMerchImage}
                    onChange={(e) => setNewMerchImage(e.target.value)}
                    placeholder="https://..."
                    className="w-full px-3 py-2 bg-[#1a1b1e] border border-white/10 rounded-lg text-white placeholder-white/40 focus:outline-none focus:border-[#00d4aa] text-sm"
                  />
                </div>
                <div>
                  <label className="block text-white/60 text-sm mb-1">Product Link</label>
                  <input
                    type="url"
                    value={newMerchLink}
                    onChange={(e) => setNewMerchLink(e.target.value)}
                    placeholder="Shopify link..."
                    className="w-full px-3 py-2 bg-[#1a1b1e] border border-white/10 rounded-lg text-white placeholder-white/40 focus:outline-none focus:border-[#00d4aa] text-sm"
                  />
                </div>
                <div className="flex items-end">
                  <Button type="submit" className="w-full bg-[#00d4aa] hover:bg-[#33e0bb] text-[#0a0b0c] font-bold">
                    Add Product
                  </Button>
                </div>
              </form>
            </div>

            {/* Merch Products List */}
            <div className="bg-[#131416] border border-white/10 rounded-xl overflow-hidden">
              <div className="p-4 border-b border-white/10">
                <h3 className="text-lg font-semibold text-white">Merch Products ({merchProducts.length})</h3>
                <p className="text-white/60 text-sm">Manage products in the Merch Zone</p>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 p-4">
                {merchProducts.map(product => (
                  <div key={product.product_id} className="relative group">
                    <div className="aspect-square rounded-lg overflow-hidden bg-[#1a1b1e]">
                      {product.image ? (
                        <img 
                          src={product.image}
                          alt={product.name}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            e.target.src = 'https://via.placeholder.com/200x200?text=No+Image';
                          }}
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <ShoppingBag className="w-8 h-8 text-white/20" />
                        </div>
                      )}
                    </div>
                    {/* Delete overlay */}
                    <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center rounded-lg">
                      <Button
                        size="sm"
                        variant="destructive"
                        onClick={() => deleteMerchProduct(product.product_id)}
                        className="bg-red-600 hover:bg-red-700"
                      >
                        <Trash2 className="w-4 h-4 mr-1" /> Delete
                      </Button>
                    </div>
                    {/* Info */}
                    <div className="mt-2">
                      <p className="text-white font-medium text-sm truncate">{product.name}</p>
                      <p className="text-[#00d4aa] font-bold">${parseFloat(product.price).toFixed(2)}</p>
                    </div>
                  </div>
                ))}
              </div>
              {merchProducts.length === 0 && (
                <p className="text-white/40 text-center py-8">No merch products yet. Add your first product above!</p>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Edit Player Modal */}
      {editingPlayer && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-[#131416] border border-white/10 rounded-xl max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-white">Edit Player: {editingPlayer.name}</h3>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => { setEditingPlayer(null); setEditFormData({}); }}
                className="text-white/60 hover:text-white"
              >
                <X className="w-5 h-5" />
              </Button>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-white/70 text-sm mb-1">Name</label>
                <Input
                  value={editFormData.name || ''}
                  onChange={(e) => setEditFormData({...editFormData, name: e.target.value})}
                  className="bg-[#1a1b1e] border-white/10 text-white"
                />
              </div>
              <div>
                <label className="block text-white/70 text-sm mb-1">Nickname</label>
                <Input
                  value={editFormData.nickname || ''}
                  onChange={(e) => setEditFormData({...editFormData, nickname: e.target.value})}
                  className="bg-[#1a1b1e] border-white/10 text-white"
                />
              </div>
              <div>
                <label className="block text-white/70 text-sm mb-1">Location</label>
                <Input
                  value={editFormData.location || ''}
                  onChange={(e) => setEditFormData({...editFormData, location: e.target.value})}
                  className="bg-[#1a1b1e] border-white/10 text-white"
                />
              </div>
              <div>
                <label className="block text-white/70 text-sm mb-1">Experience Level</label>
                <select
                  value={editFormData.experience_level || 'Beginner'}
                  onChange={(e) => setEditFormData({...editFormData, experience_level: e.target.value})}
                  className="w-full bg-[#1a1b1e] border border-white/10 text-white rounded-md px-3 py-2"
                >
                  <option value="Beginner">Beginner</option>
                  <option value="Intermediate">Intermediate</option>
                  <option value="Advanced">Advanced</option>
                  <option value="Professional">Professional</option>
                </select>
              </div>
              <div>
                <label className="block text-white/70 text-sm mb-1">Favorite Position</label>
                <select
                  value={editFormData.favorite_position || ''}
                  onChange={(e) => setEditFormData({...editFormData, favorite_position: e.target.value})}
                  className="w-full bg-[#1a1b1e] border border-white/10 text-white rounded-md px-3 py-2"
                >
                  <option value="">Select Position</option>
                  <option value="Front">Front</option>
                  <option value="Mid">Mid</option>
                  <option value="Back">Back</option>
                  <option value="Snake">Snake</option>
                  <option value="Dorito">Dorito</option>
                  <option value="Insert">Insert</option>
                  <option value="Flex">Flex</option>
                </select>
              </div>
              <div>
                <label className="block text-white/70 text-sm mb-1">Play Style</label>
                <select
                  value={editFormData.play_style || ''}
                  onChange={(e) => setEditFormData({...editFormData, play_style: e.target.value})}
                  className="w-full bg-[#1a1b1e] border border-white/10 text-white rounded-md px-3 py-2"
                >
                  <option value="">Select Style</option>
                  <option value="Aggressive">Aggressive</option>
                  <option value="Defensive">Defensive</option>
                  <option value="Support">Support</option>
                  <option value="Balanced">Balanced</option>
                </select>
              </div>
              <div>
                <label className="block text-white/70 text-sm mb-1">Years Playing</label>
                <Input
                  type="number"
                  value={editFormData.years_playing || ''}
                  onChange={(e) => setEditFormData({...editFormData, years_playing: e.target.value})}
                  className="bg-[#1a1b1e] border-white/10 text-white"
                  min="0"
                />
              </div>
              <div>
                <label className="block text-white/70 text-sm mb-1">Primary Marker</label>
                <Input
                  value={editFormData.primary_marker || ''}
                  onChange={(e) => setEditFormData({...editFormData, primary_marker: e.target.value})}
                  className="bg-[#1a1b1e] border-white/10 text-white"
                />
              </div>
              <div>
                <label className="block text-white/70 text-sm mb-1">Home Field</label>
                <Input
                  value={editFormData.home_field || ''}
                  onChange={(e) => setEditFormData({...editFormData, home_field: e.target.value})}
                  className="bg-[#1a1b1e] border-white/10 text-white"
                />
              </div>
              <div>
                <label className="block text-white/70 text-sm mb-1">Instagram Handle</label>
                <Input
                  value={editFormData.instagram_handle || ''}
                  onChange={(e) => setEditFormData({...editFormData, instagram_handle: e.target.value})}
                  placeholder="@username"
                  className="bg-[#1a1b1e] border-white/10 text-white"
                />
              </div>
              <div>
                <label className="block text-white/70 text-sm mb-1">Championships</label>
                <Input
                  value={editFormData.championships || ''}
                  onChange={(e) => setEditFormData({...editFormData, championships: e.target.value})}
                  className="bg-[#1a1b1e] border-white/10 text-white"
                />
              </div>
              <div>
                <label className="block text-white/70 text-sm mb-1">Awards</label>
                <Input
                  value={editFormData.awards || ''}
                  onChange={(e) => setEditFormData({...editFormData, awards: e.target.value})}
                  className="bg-[#1a1b1e] border-white/10 text-white"
                />
              </div>
              <div>
                <label className="block text-white/70 text-sm mb-1">Past Teams</label>
                <Input
                  value={editFormData.past_teams || ''}
                  onChange={(e) => setEditFormData({...editFormData, past_teams: e.target.value})}
                  className="bg-[#1a1b1e] border-white/10 text-white"
                />
              </div>
              <div>
                <label className="block text-white/70 text-sm mb-1">Privacy</label>
                <select
                  value={editFormData.privacy || 'public'}
                  onChange={(e) => setEditFormData({...editFormData, privacy: e.target.value})}
                  className="w-full bg-[#1a1b1e] border border-white/10 text-white rounded-md px-3 py-2"
                >
                  <option value="public">Public</option>
                  <option value="private">Private</option>
                </select>
              </div>
              <div className="md:col-span-2">
                <label className="block text-white/70 text-sm mb-1">Bio</label>
                <textarea
                  value={editFormData.bio || ''}
                  onChange={(e) => setEditFormData({...editFormData, bio: e.target.value})}
                  rows={3}
                  className="w-full bg-[#1a1b1e] border border-white/10 text-white rounded-md px-3 py-2 resize-none"
                />
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <Button
                variant="outline"
                onClick={() => { setEditingPlayer(null); setEditFormData({}); }}
                className="flex-1 border-white/20 text-white hover:bg-white/5"
              >
                Cancel
              </Button>
              <Button
                onClick={savePlayerEdits}
                className="flex-1 bg-[#00d4aa] hover:bg-[#33e0bb] text-[#0a0b0c] font-bold"
              >
                <Save className="w-4 h-4 mr-2" /> Save Changes
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Team Modal */}
      {editingTeam && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-[#131416] border border-white/10 rounded-xl max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-white">Edit Team: {editingTeam.name}</h3>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => { setEditingTeam(null); setEditFormData({}); }}
                className="text-white/60 hover:text-white"
              >
                <X className="w-5 h-5" />
              </Button>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-white/70 text-sm mb-1">Team Name</label>
                <Input
                  value={editFormData.name || ''}
                  onChange={(e) => setEditFormData({...editFormData, name: e.target.value})}
                  className="bg-[#1a1b1e] border-white/10 text-white"
                />
              </div>
              <div>
                <label className="block text-white/70 text-sm mb-1">Location</label>
                <Input
                  value={editFormData.location || ''}
                  onChange={(e) => setEditFormData({...editFormData, location: e.target.value})}
                  className="bg-[#1a1b1e] border-white/10 text-white"
                />
              </div>
              <div>
                <label className="block text-white/70 text-sm mb-1">Division</label>
                <select
                  value={editFormData.division || ''}
                  onChange={(e) => setEditFormData({...editFormData, division: e.target.value})}
                  className="w-full bg-[#1a1b1e] border border-white/10 text-white rounded-md px-3 py-2"
                >
                  <option value="">Select Division</option>
                  <option value="D5">D5</option>
                  <option value="D4">D4</option>
                  <option value="D3">D3</option>
                  <option value="D2">D2</option>
                  <option value="D1">D1</option>
                  <option value="Professional">Professional</option>
                </select>
              </div>
              <div>
                <label className="block text-white/70 text-sm mb-1">League</label>
                <Input
                  value={editFormData.league || ''}
                  onChange={(e) => setEditFormData({...editFormData, league: e.target.value})}
                  placeholder="e.g., NXL, WDP, APPA"
                  className="bg-[#1a1b1e] border-white/10 text-white"
                />
              </div>
              <div>
                <label className="block text-white/70 text-sm mb-1">Home Field</label>
                <Input
                  value={editFormData.home_field || ''}
                  onChange={(e) => setEditFormData({...editFormData, home_field: e.target.value})}
                  className="bg-[#1a1b1e] border-white/10 text-white"
                />
              </div>
              <div>
                <label className="block text-white/70 text-sm mb-1">Established Year</label>
                <Input
                  type="number"
                  value={editFormData.established_year || ''}
                  onChange={(e) => setEditFormData({...editFormData, established_year: e.target.value})}
                  placeholder="e.g., 2020"
                  className="bg-[#1a1b1e] border-white/10 text-white"
                />
              </div>
              <div>
                <label className="block text-white/70 text-sm mb-1">Roster Size</label>
                <Input
                  type="number"
                  value={editFormData.roster_size || ''}
                  onChange={(e) => setEditFormData({...editFormData, roster_size: e.target.value})}
                  className="bg-[#1a1b1e] border-white/10 text-white"
                />
              </div>
              <div>
                <label className="block text-white/70 text-sm mb-1">Primary Color</label>
                <Input
                  value={editFormData.primary_color || ''}
                  onChange={(e) => setEditFormData({...editFormData, primary_color: e.target.value})}
                  placeholder="e.g., #00d4aa or Red"
                  className="bg-[#1a1b1e] border-white/10 text-white"
                />
              </div>
              <div>
                <label className="block text-white/70 text-sm mb-1">Team Logo URL</label>
                <Input
                  value={editFormData.team_logo || ''}
                  onChange={(e) => setEditFormData({...editFormData, team_logo: e.target.value})}
                  placeholder="https://..."
                  className="bg-[#1a1b1e] border-white/10 text-white"
                />
              </div>
              <div>
                <label className="block text-white/70 text-sm mb-1">Website</label>
                <Input
                  value={editFormData.website || ''}
                  onChange={(e) => setEditFormData({...editFormData, website: e.target.value})}
                  placeholder="https://..."
                  className="bg-[#1a1b1e] border-white/10 text-white"
                />
              </div>
              <div>
                <label className="block text-white/70 text-sm mb-1">Instagram Handle</label>
                <Input
                  value={editFormData.instagram_handle || ''}
                  onChange={(e) => setEditFormData({...editFormData, instagram_handle: e.target.value})}
                  placeholder="@teamhandle"
                  className="bg-[#1a1b1e] border-white/10 text-white"
                />
              </div>
              <div>
                <label className="block text-white/70 text-sm mb-1">Achievements</label>
                <Input
                  value={editFormData.achievements || ''}
                  onChange={(e) => setEditFormData({...editFormData, achievements: e.target.value})}
                  placeholder="e.g., NXL Champions 2024"
                  className="bg-[#1a1b1e] border-white/10 text-white"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-white/70 text-sm mb-1">Bio / Description</label>
                <textarea
                  value={editFormData.bio || ''}
                  onChange={(e) => setEditFormData({...editFormData, bio: e.target.value})}
                  rows={3}
                  className="w-full bg-[#1a1b1e] border border-white/10 text-white rounded-md px-3 py-2 resize-none"
                />
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <Button
                variant="outline"
                onClick={() => { setEditingTeam(null); setEditFormData({}); }}
                className="flex-1 border-white/20 text-white hover:bg-white/5"
              >
                Cancel
              </Button>
              <Button
                onClick={saveTeamEdits}
                className="flex-1 bg-[#00d4aa] hover:bg-[#33e0bb] text-[#0a0b0c] font-bold"
              >
                <Save className="w-4 h-4 mr-2" /> Save Changes
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Media Modal */}
      {editingMedia && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-[#131416] border border-white/10 rounded-xl max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-white">Edit Media</h3>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => { setEditingMedia(null); setMediaEditData({}); }}
                className="text-white/60 hover:text-white"
              >
                <X className="w-5 h-5" />
              </Button>
            </div>

            {/* Preview */}
            <div className="mb-6">
              <div className="aspect-video rounded-lg overflow-hidden bg-[#1a1b1e]">
                <img 
                  src={(editingMedia.url || editingMedia.file_path)?.startsWith('/api') ? `${API_URL}${editingMedia.url || editingMedia.file_path}` : (editingMedia.url || editingMedia.file_path)}
                  alt="Preview"
                  className="w-full h-full object-contain"
                  onError={(e) => {
                    e.target.src = 'https://via.placeholder.com/400x300?text=Preview';
                  }}
                />
              </div>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-white/70 text-sm mb-1">Caption</label>
                <Input
                  value={mediaEditData.caption || ''}
                  onChange={(e) => setMediaEditData({...mediaEditData, caption: e.target.value})}
                  placeholder="Photo caption..."
                  className="bg-[#1a1b1e] border-white/10 text-white"
                />
              </div>
              
              <div>
                <label className="block text-white/70 text-sm mb-1">Team Name</label>
                <Input
                  value={mediaEditData.team_name || ''}
                  onChange={(e) => setMediaEditData({...mediaEditData, team_name: e.target.value})}
                  placeholder="e.g., NY Aces"
                  className="bg-[#1a1b1e] border-white/10 text-white"
                />
              </div>
              
              <div>
                <label className="block text-white/70 text-sm mb-1">Tags (comma separated)</label>
                <Input
                  value={mediaEditData.tags || ''}
                  onChange={(e) => setMediaEditData({...mediaEditData, tags: e.target.value})}
                  placeholder="e.g., tournament, action, nyaces"
                  className="bg-[#1a1b1e] border-white/10 text-white"
                />
              </div>
              
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="is_sponsored"
                  checked={mediaEditData.is_sponsored || false}
                  onChange={(e) => setMediaEditData({...mediaEditData, is_sponsored: e.target.checked})}
                  className="w-5 h-5 rounded bg-[#1a1b1e] border-white/10 text-[#00d4aa]"
                />
                <label htmlFor="is_sponsored" className="text-white cursor-pointer">
                  Mark as <span className="text-[#00d4aa] font-semibold">SPONSORED</span>
                </label>
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <Button
                variant="outline"
                onClick={() => { setEditingMedia(null); setMediaEditData({}); }}
                className="flex-1 border-white/20 text-white hover:bg-white/5"
              >
                Cancel
              </Button>
              <Button
                onClick={saveMediaEdits}
                className="flex-1 bg-[#00d4aa] hover:bg-[#33e0bb] text-[#0a0b0c] font-bold"
              >
                <Save className="w-4 h-4 mr-2" /> Save Changes
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
